import { SIELESection } from './types';

/**
 * Section 介绍文案接口
 */
export interface SectionIntro {
  sectionId: string;
  title: string;
  description: string;
  tips?: string[];
  duration?: number;
}

/**
 * SIELE 阅读理解介绍
 */
const SIELE_READING_INTRO: SectionIntro = {
  sectionId: SIELESection.READING,
  title: '阅读理解 (Comprensión de lectura)',
  description: 'SIELE 阅读部分测试您对西班牙语书面文本的理解能力。阅读材料包括文章、广告、通知、电子邮件、学术论文等，内容涵盖日常生活、社会文化、工作学习等多个领域。',
  duration: 60,
  tips: [
    '先快速浏览全文，了解文章主题和结构',
    '仔细阅读题目，明确需要查找的信息',
    '注意关键词和同义词替换',
    '理解文章的逻辑关系，如因果关系、转折关系等',
    '注意文章的语气和作者的态度',
    '遇到生词时，尝试通过上下文推断词义',
    '合理分配时间，不要在单个题目上花费过多时间',
    '注意西班牙语的性别、数变化和时态变化',
  ],
};

/**
 * SIELE 听力理解介绍
 */
const SIELE_LISTENING_INTRO: SectionIntro = {
  sectionId: SIELESection.LISTENING,
  title: '听力理解 (Comprensión auditiva)',
  description: 'SIELE 听力部分测试您对西班牙语口语的理解能力。听力材料包括对话、访谈、新闻报道、广播、讲座等，涵盖日常生活、工作、学习、社会文化等不同场景。',
  duration: 55,
  tips: [
    '在音频播放前，先快速浏览题目，了解需要关注的信息',
    '注意听力材料中的关键词和语调变化',
    '注意区分相似发音的词汇，避免混淆',
    '如果第一次没听清楚，不要纠结，继续听下面的内容',
    '关注对话者的语气和情感色彩，有助于理解语境',
    '注意西班牙语的连读和语速变化',
    '理解口语中的省略和简化表达',
  ],
};

/**
 * SIELE 书面表达介绍
 */
const SIELE_WRITING_INTRO: SectionIntro = {
  sectionId: SIELESection.WRITING,
  title: '书面表达 (Expresión e interacción escritas)',
  description: 'SIELE 写作部分测试您的西班牙语书面表达能力。包括电子邮件、书信、短文、文章等不同类型的写作任务，测试词汇、语法、拼写和组织能力。',
  duration: 50,
  tips: [
    '仔细阅读题目要求，明确写作任务类型',
    '先花2-3分钟规划文章结构或提纲',
    '确保字数符合要求',
    '使用多样化的句式和词汇，展现语言能力',
    '注意段落结构：开头段、主体段、结尾段',
    '使用恰当的连接词，使文章逻辑清晰',
    '注意西班牙语的性数配合和时态使用',
    '确保语体与任务要求一致（正式/非正式）',
    '留2-3分钟检查语法和拼写错误',
  ],
};

/**
 * SIELE 口语表达介绍
 */
const SIELE_SPEAKING_INTRO: SectionIntro = {
  sectionId: SIELESection.SPEAKING,
  title: '口语表达 (Expresión e interacción orales)',
  description: 'SIELE 口语部分是与考官一对一或小组的面试，测试您的西班牙语口语表达能力。包括对话、描述、讨论、表达观点等形式，评估发音、流利度、词汇和语法的准确性。',
  duration: 20,
  tips: [
    '自信地表达，即使犯错也不要紧张，继续说下去',
    '注意发音清晰，特别是西班牙语的颤音r/rr',
    '使用多样的词汇和语法结构',
    '注意时态的正确使用，特别是虚拟式的使用',
    '积极参与对话，展现交流能力',
    '如果听不清问题，可以礼貌地请考官重复',
    '注意语速适中，不要过快或过慢',
    '练习时录音回听，自我改进',
    '注意语体和语境的匹配',
    '在小组讨论中，尊重他人发言，适时补充自己的观点',
  ],
};

/**
 * SIELE Section 介绍文案映射
 */
export const SIELE_SECTION_INTROS: Record<string, SectionIntro> = {
  [SIELESection.READING]: SIELE_READING_INTRO,
  [SIELESection.LISTENING]: SIELE_LISTENING_INTRO,
  [SIELESection.WRITING]: SIELE_WRITING_INTRO,
  [SIELESection.SPEAKING]: SIELE_SPEAKING_INTRO,
};

/**
 * 获取 SIELE Section 介绍文案
 */
export function getSIELESectionIntro(sectionId: string): SectionIntro | null {
  return SIELE_SECTION_INTROS[sectionId] || null;
}
